import React from "react";
import { Shield, Sparkles, Scale, Leaf } from "lucide-react";

const YALI_IMG = "/yali-guardian.jpg";

const PILLARS = [
    {
        Icon: Shield,
        title: "Protection",
        body: "Safeguarding planetary health through responsible innovations.",
    },
    {
        Icon: Leaf,
        title: "Integration",
        body: "Combining biology, chemistry, and engineering disciplines.",
    },
    {
        Icon: Scale,
        title: "Balance",
        body: "Working with nature for sustainable development.",
    },
    {
        Icon: Sparkles,
        title: "Innovation",
        body: "Creating environmentally responsible biotech solutions.",
    },
];

export const Story = () => {
    return (
        <section
            id="story"
            data-testid="story-section"
            className="section-pad relative overflow-hidden"
        >
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-moss))]/15"
                style={{ width: 600, height: 600, top: 100, right: -200 }}
            />

            <div className="container-x relative">
                <div className="max-w-3xl">
                    <p className="overline">
                        <span className="eyebrow-line" />
                        Our story
                    </p>
                    <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.02]">
                        The spirit of the{" "}
                        <em className="not-italic text-primary">Yali</em>.
                    </h2>
                </div>

                <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    {/* Yali Guardian image panel */}
                    <div className="lg:col-span-5 lg:sticky lg:top-28">
                        <div className="relative">
                            <div className="absolute -inset-4 border border-border rounded-[2rem] -z-10" />
                            <div className="absolute -inset-1 rounded-[1.75rem] bg-gradient-to-b from-primary/8 to-transparent -z-10" />

                            <div className="relative overflow-hidden rounded-[1.5rem] aspect-square ring-1 ring-border shadow-2xl shadow-primary/10 bg-[#0E2417]">
                                <img
                                    src={YALI_IMG}
                                    alt="Stone Yali sculpture — temple guardian — framed by a glowing DNA helix"
                                    className="absolute inset-0 w-full h-full object-cover hover:scale-[1.04] transition-transform duration-[2000ms] ease-out"
                                />

                                {/* Subtle vignette for depth */}
                                <div
                                    aria-hidden
                                    className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10 pointer-events-none"
                                />

                                {/* "Inspired by" tag */}
                                <div className="absolute bottom-4 right-4 rounded-xl border border-white/15 bg-black/55 backdrop-blur-md px-4 py-2.5">
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--brand-moss))] font-semibold">
                                        Inspired by
                                    </p>
                                    <p className="font-display text-[17px] text-white leading-tight tracking-tight">
                                        The Yali Guardian
                                    </p>
                                </div>
                            </div>

                            <div className="mt-4 flex justify-between items-center text-xs uppercase tracking-[0.22em] text-muted-foreground">
                                <span>Mythology · meets · science</span>
                                <span>Yali · guardian</span>
                            </div>
                        </div>
                    </div>

                    {/* Narrative + pillars */}
                    <div className="lg:col-span-7">
                        <p className="text-base md:text-lg leading-relaxed text-foreground/85">
                            Our biotech startup draws inspiration from the{" "}
                            <span className="text-foreground font-medium">Yali</span>, a
                            powerful composite guardian often seen in temples across India.
                            Just as the Yali combines the strengths of different creatures
                            to symbolize protection, intelligence, and balance, Yaalee
                            Biotech Solutions integrates multiple scientific disciplines to
                            develop sustainable biotech products.
                        </p>
                        <p className="mt-5 text-base md:text-lg leading-relaxed text-foreground/85">
                            Compared to Greek hybrid creatures like the Chimera or the
                            Griffin, the Yali uniquely represents{" "}
                            <span className="text-primary font-medium">
                                harmony with nature
                            </span>
                            . This reflects our vision of building biotechnology solutions
                            that work with nature to create a more sustainable future.
                        </p>

                        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {PILLARS.map(({ Icon, title, body }, i) => (
                                <div
                                    key={title}
                                    data-testid={`story-pillar-${i}`}
                                    className="group rounded-2xl border border-border bg-card/60 backdrop-blur-sm p-6 hover:border-primary/60 transition-colors"
                                >
                                    <div className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center mb-5 group-hover:bg-primary group-hover:text-primary-foreground transition">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <h3 className="font-display text-2xl font-medium tracking-tight">
                                        {title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                                        {body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Story;
